import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  fetchAffiliates,
  searchPlatformUserForAffiliate,
  createAffiliate,
  updateAffiliate,
} from '@/lib/affiliates'
import { buildMyAffiliateLink } from '@/lib/my-affiliate'
import { ApiClientError } from '@/lib/api/client'
import {
  applyDoeyUserIdAfterUserSearch,
  canSubmitCredentialFormWithDoeyPayout,
  DOEY_GATEWAY_REQUIRED_MESSAGE,
} from '@/lib/institution-doey-payout'
import { useInstitutionCampaignSelect } from '@/hooks/useInstitutionCampaignSelect'
import type { Campaign } from '@/lib/campaigns'
import type {
  Affiliate,
  AffiliateAllowedCampaign,
  AffiliatePayoutMethod,
  CreateAffiliateInput,
  PlatformUser,
  UpdateAffiliateInput,
} from '@/types/affiliates'
import { HorizontalScrollWithArrows } from '@/components/HorizontalScrollWithArrows'
import { AffiliateLinkDistributorModal } from '@/pages/AfiliadosPage/AffiliateLinkDistributorModal'
import { AffiliateLinksModal } from '@/pages/AfiliadosPage/AffiliateLinksModal'
import styles from './AfiliadosListaPage.module.css'

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function filterActiveCampaignsWithSlug(campaigns: Campaign[]): Campaign[] {
  return campaigns.filter(
    (c) => c.status === 'active' && typeof c.slug === 'string' && c.slug.trim() !== ''
  )
}

function parseCommissionPercent(value: string): number | null {
  const num = parseFloat(value.replace(',', '.'))
  if (Number.isNaN(num) || num < 0.1 || num > 80) {
    return null
  }
  return num
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface AffiliateModalProps {
  affiliate: Affiliate | null
  activeCampaigns: Campaign[]
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (data: CreateAffiliateInput | UpdateAffiliateInput, id?: number) => Promise<void>
}

function AffiliateModal({
  affiliate,
  activeCampaigns,
  isSubmitting,
  onClose,
  onSubmit,
}: AffiliateModalProps) {
  const isEdit = affiliate !== null

  const [searchEmail, setSearchEmail] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null)
  const [isSearchingUser, setIsSearchingUser] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [payoutMethod, setPayoutMethod] = useState<AffiliatePayoutMethod>(affiliate?.payout_method ?? 'doey')
  const [doeyUserId, setDoeyUserId] = useState(affiliate?.doey_user_id ?? '')
  const [commissionPercent, setCommissionPercent] = useState(
    affiliate ? String(affiliate.commission_percent) : ''
  )
  const [isActive, setIsActive] = useState(affiliate?.is_active ?? true)
  const [allCampaigns, setAllCampaigns] = useState(affiliate?.all_campaigns ?? true)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>(
    () => affiliate?.allowed_campaigns.map((c) => c.id) ?? []
  )
  const [campaignCommissions, setCampaignCommissions] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {}
    for (const c of affiliate?.allowed_campaigns ?? []) {
      if (c.commission_percent !== undefined) {
        initial[c.id] = String(c.commission_percent)
      }
    }
    return initial
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [gatewayAlert, setGatewayAlert] = useState<string | null>(null)

  useEffect(() => {
    const sync = window.setTimeout(() => {
      if (affiliate) {
        setPayoutMethod(affiliate.payout_method)
        setDoeyUserId(
          affiliate.payout_method === 'doey'
            ? affiliate.gateway_doey_user_id ?? affiliate.doey_user_id ?? ''
            : ''
        )
        setCommissionPercent(String(affiliate.commission_percent))
        setIsActive(affiliate.is_active)
        setAllCampaigns(affiliate.all_campaigns ?? true)
        setSelectedCampaignIds((affiliate.allowed_campaigns ?? []).map((c) => c.id))
        const commissions: Record<number, string> = {}
        for (const c of affiliate.allowed_campaigns ?? []) {
          if (c.commission_percent !== undefined) {
            commissions[c.id] = String(c.commission_percent)
          }
        }
        setCampaignCommissions(commissions)
      } else {
        setPayoutMethod('doey')
        setDoeyUserId('')
        setCommissionPercent('')
        setIsActive(true)
        setAllCampaigns(true)
        setSelectedCampaignIds([])
        setCampaignCommissions({})
        setSelectedUser(null)
        setSearchEmail('')
        setSearchPhone('')
        setSearchError(null)
        setGatewayAlert(null)
      }
    }, 0)
    return () => window.clearTimeout(sync)
  }, [affiliate])

  useEffect(() => {
    if (payoutMethod !== 'doey') {
      setDoeyUserId('')
      setGatewayAlert(null)
      return
    }
    if (selectedUser) {
      const doeyApply = applyDoeyUserIdAfterUserSearch(payoutMethod, selectedUser.doey_user_id)
      setDoeyUserId(doeyApply.ok ? doeyApply.doeyUserId : '')
      setGatewayAlert(doeyApply.ok ? null : doeyApply.gatewayError)
      return
    }
    setDoeyUserId(affiliate ? affiliate.gateway_doey_user_id ?? affiliate.doey_user_id ?? '' : '')
    setGatewayAlert(null)
  }, [affiliate, isEdit, payoutMethod, selectedUser])

  const handleSearchUser = async () => {
    const email = searchEmail.trim()
    const phone = searchPhone.replace(/\D/g, '')
    if (!email && !phone) {
      setSearchError('Informe o e-mail ou o telefone para procurar.')
      setSelectedUser(null)
      return
    }
    setIsSearchingUser(true)
    setSearchError(null)
    try {
      const res = await searchPlatformUserForAffiliate({
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      })
      setSelectedUser(res.user)
      const doeyApply = applyDoeyUserIdAfterUserSearch(payoutMethod, res.user.doey_user_id)
      if (!doeyApply.ok) {
        setGatewayAlert(doeyApply.gatewayError)
        setDoeyUserId('')
      } else {
        setGatewayAlert(null)
        if (payoutMethod === 'doey') {
          setDoeyUserId(doeyApply.doeyUserId)
        }
      }
    } catch (err) {
      setSelectedUser(null)
      setGatewayAlert(null)
      if (err instanceof ApiClientError) {
        setSearchError(err.message)
      } else {
        setSearchError('Usuário não encontrado.')
      }
    } finally {
      setIsSearchingUser(false)
    }
  }

  const globalCommissionNum = parseCommissionPercent(commissionPercent)
  const isGlobalCommissionValid = globalCommissionNum !== null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (!isEdit && !selectedUser) {
      setFormError('Procure e selecione um usuário da plataforma.')
      return
    }
    if (
      !canSubmitCredentialFormWithDoeyPayout(
        isEdit,
        payoutMethod,
        selectedUser?.doey_user_id ?? doeyUserId,
        affiliate?.payout_method,
        affiliate?.doey_user_id
      )
    ) {
      setFormError(DOEY_GATEWAY_REQUIRED_MESSAGE)
      return
    }
    if (allCampaigns) {
      if (!isGlobalCommissionValid) {
        setFormError('A comissão deve ser entre 0,1% e 80%.')
        return
      }
    } else {
      if (selectedCampaignIds.length === 0) {
        setFormError('Selecione ao menos uma campanha ou marque Todas as Campanhas.')
        return
      }
      for (const campaignId of selectedCampaignIds) {
        const pct = parseCommissionPercent(campaignCommissions[campaignId] ?? '')
        if (pct === null) {
          setFormError('Informe a comissão (0,1% a 80%) para cada campanha selecionada.')
          return
        }
      }
    }

    const campaignsPayload = allCampaigns
      ? undefined
      : selectedCampaignIds.map((campaignId) => ({
          campaign_id: campaignId,
          commission_percent: parseCommissionPercent(campaignCommissions[campaignId] ?? '')!,
        }))

    if (isEdit) {
      const updateData: UpdateAffiliateInput = {
        is_active: isActive,
        payout_method: payoutMethod,
        all_campaigns: allCampaigns,
        ...(allCampaigns && globalCommissionNum !== null
          ? { commission_percent: globalCommissionNum }
          : {}),
        ...(campaignsPayload ? { campaigns: campaignsPayload } : {}),
      }
      await onSubmit(updateData, affiliate.id)
    } else {
      const createData: CreateAffiliateInput = {
        platform_user_id: selectedUser!.id,
        payout_method: payoutMethod,
        all_campaigns: allCampaigns,
        ...(allCampaigns && globalCommissionNum !== null
          ? { commission_percent: globalCommissionNum }
          : {}),
        ...(campaignsPayload ? { campaigns: campaignsPayload } : {}),
      }
      await onSubmit(createData)
    }
  }

  const toggleCampaignId = (campaignId: number) => {
    setSelectedCampaignIds((prev) => {
      if (prev.includes(campaignId)) {
        setCampaignCommissions((commissions) => {
          const next = { ...commissions }
          delete next[campaignId]
          return next
        })
        return prev.filter((id) => id !== campaignId)
      }
      return [...prev, campaignId]
    })
  }

  const setCampaignCommission = (campaignId: number, value: string) => {
    setCampaignCommissions((prev) => ({ ...prev, [campaignId]: value }))
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="affiliate-modal-title">
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 id="affiliate-modal-title" className={styles.modalTitle}>
            {isEdit ? 'Editar Afiliado' : 'Novo Afiliado'}
          </h3>
          <button className={styles.modalClose} onClick={onClose} aria-label="Fechar">
            <Icon icon="mdi:close" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className={styles.modalBody}>
            {!isEdit && (
              <div className={styles.field}>
                <span className={styles.label}>Usuário da plataforma *</span>
                <p className={styles.searchHint}>
                  Informe o e-mail ou o telefone do cadastro e clique em Procurar.
                </p>
                <div className={styles.userSearchRow}>
                  <input
                    className={styles.input}
                    type="email"
                    placeholder="E-mail"
                    value={searchEmail}
                    onChange={(e) => {
                      setSearchEmail(e.target.value)
                      setSearchError(null)
                    }}
                    autoComplete="off"
                  />
                  <input
                    className={styles.input}
                    type="tel"
                    placeholder="Telefone"
                    value={searchPhone}
                    onChange={(e) => {
                      setSearchPhone(e.target.value)
                      setSearchError(null)
                    }}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className={styles.searchUserBtn}
                    disabled={isSearchingUser}
                    onClick={() => void handleSearchUser()}
                  >
                    {isSearchingUser ? 'Procurando…' : 'Procurar'}
                  </button>
                </div>
                {searchError ? (
                  <p className={styles.searchError} role="alert">
                    {searchError}
                  </p>
                ) : null}
                {selectedUser ? (
                  <div className={styles.selectedDonor}>
                    <span>
                      {selectedUser.name}
                      {selectedUser.email ? ` — ${selectedUser.email}` : ''}
                      {selectedUser.phone ? ` — ${selectedUser.phone}` : ''}
                    </span>
                    <button
                      type="button"
                      className={styles.clearDonor}
                      onClick={() => {
                        setSelectedUser(null)
                        setSearchError(null)
                      }}
                    >
                      <Icon icon="mdi:close-circle" />
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {/* Afiliado nome (somente edição) */}
            {isEdit && (
              <div className={styles.field}>
                <label className={styles.label}>Afiliado</label>
                <div className={styles.readonlyField}>{affiliate.affiliate_name}</div>
              </div>
            )}

            {/* Modo de pagamento */}
            <div className={styles.field}>
              <span className={styles.label} id="affiliate-payout-label">
                Pagamento da comissão *
              </span>
              <div
                className={styles.payoutGrid}
                role="radiogroup"
                aria-labelledby="affiliate-payout-label"
              >
                <label
                  className={`${styles.payoutCard} ${payoutMethod === 'doey' ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="payout"
                    className={styles.payoutInputHidden}
                    checked={payoutMethod === 'doey'}
                    onChange={() => setPayoutMethod('doey')}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.payoutIconDoey}`}>
                      <Icon icon="mdi:flash" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Doey automático</span>
                      <span className={styles.payoutSub}>Split no PIX</span>
                    </span>
                  </span>
                </label>
                <label
                  className={`${styles.payoutCard} ${payoutMethod === 'manual' ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="payout"
                    className={styles.payoutInputHidden}
                    checked={payoutMethod === 'manual'}
                    onChange={() => setPayoutMethod('manual')}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.payoutIconManual}`}>
                      <Icon icon="mdi:wallet-outline" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Manual</span>
                      <span className={styles.payoutSub}>Saldo interno</span>
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Doey User ID */}
            {payoutMethod === 'doey' && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="doey-user-id">
                  ID Doey *
                </label>
                <input
                  id="doey-user-id"
                  className={styles.input}
                  placeholder="Preenchido pelo Gateway de Pagamento"
                  maxLength={36}
                  value={doeyUserId}
                  readOnly
                  disabled
                  aria-readonly="true"
                />
                <p className={styles.fieldHint}>
                  Preenchido automaticamente pelo Gateway de Pagamento do usuário pesquisado.
                </p>
                {gatewayAlert && (
                  <div className={styles.formError} role="alert">
                    <Icon icon="mdi:alert-circle-outline" aria-hidden />
                    {gatewayAlert}
                  </div>
                )}
              </div>
            )}

            {/* Campanhas autorizadas */}
            <div className={styles.field}>
              <span className={styles.label} id="affiliate-campaigns-label">
                Campanhas autorizadas *
              </span>
              <div
                className={styles.payoutGrid}
                role="radiogroup"
                aria-labelledby="affiliate-campaigns-label"
              >
                <label
                  className={`${styles.payoutCard} ${allCampaigns ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="campaign-access"
                    className={styles.payoutInputHidden}
                    checked={allCampaigns}
                    onChange={() => {
                      setAllCampaigns(true)
                      setSelectedCampaignIds([])
                    }}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.campaignIconAll}`}>
                      <Icon icon="mdi:bullhorn-outline" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Todas as Campanhas</span>
                      <span className={styles.payoutSub}>Qualquer campanha ativa da instituição</span>
                    </span>
                  </span>
                </label>
                <label
                  className={`${styles.payoutCard} ${!allCampaigns ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="campaign-access"
                    className={styles.payoutInputHidden}
                    checked={!allCampaigns}
                    onChange={() => setAllCampaigns(false)}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.campaignIconSpecific}`}>
                      <Icon icon="mdi:format-list-checks" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Campanhas específicas</span>
                      <span className={styles.payoutSub}>Selecione abaixo as permitidas</span>
                    </span>
                  </span>
                </label>
              </div>
              {!allCampaigns && (
                <div className={styles.campaignPickPanel}>
                  {activeCampaigns.length === 0 ? (
                    <p className={styles.campaignPickEmpty}>
                      Nenhuma campanha ativa com slug cadastrado. Ative uma campanha antes de
                      restringir o afiliado.
                    </p>
                  ) : (
                    <>
                      <p className={styles.campaignPickHint}>
                        Marque as campanhas que este afiliado pode divulgar:
                      </p>
                      <div className={styles.campaignPickGrid}>
                        {activeCampaigns.map((campaign) => {
                          const isSelected = selectedCampaignIds.includes(campaign.id)
                          const label = campaign.title?.trim() || campaign.name
                          return (
                            <div
                              key={campaign.id}
                              className={`${styles.campaignPickCard} ${isSelected ? styles.campaignPickCardActive : ''}`}
                            >
                              <label className={styles.campaignPickCardLabel}>
                                <input
                                  type="checkbox"
                                  className={styles.payoutInputHidden}
                                  checked={isSelected}
                                  onChange={() => toggleCampaignId(campaign.id)}
                                />
                                <span className={styles.campaignPickCardInner}>
                                  <Icon
                                    icon={isSelected ? 'mdi:checkbox-marked-circle' : 'mdi:checkbox-blank-circle-outline'}
                                    className={styles.campaignPickCheckIcon}
                                    aria-hidden
                                  />
                                  <span className={styles.campaignPickName}>{label}</span>
                                </span>
                              </label>
                              {isSelected && (
                                <div className={styles.campaignPickCommission}>
                                  <label
                                    className={styles.campaignPickCommissionLabel}
                                    htmlFor={`campaign-commission-${campaign.id}`}
                                  >
                                    Comissão (%)
                                  </label>
                                  <input
                                    id={`campaign-commission-${campaign.id}`}
                                    className={styles.campaignPickCommissionInput}
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    max="80"
                                    placeholder="10"
                                    value={campaignCommissions[campaign.id] ?? ''}
                                    onChange={(e) => setCampaignCommission(campaign.id, e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {allCampaigns && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="commission">
                  Comissão (%) *
                </label>
                <input
                  id="commission"
                  className={styles.input}
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="80"
                  placeholder="10"
                  value={commissionPercent}
                  onChange={(e) => setCommissionPercent(e.target.value)}
                />
                <span className={styles.fieldHint}>0,1% a 80% — vale para todas as campanhas</span>
              </div>
            )}

            {/* Status (somente edição) */}
            {isEdit && (
              <div className={styles.field}>
                <label className={styles.toggleLabel}>
                  <span>Afiliado ativo</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    className={`${styles.toggle} ${isActive ? styles.toggleOn : ''}`}
                    onClick={() => setIsActive((v) => !v)}
                  >
                    <span className={styles.toggleThumb} />
                  </button>
                </label>
              </div>
            )}

            {formError && (
              <div className={styles.formError}>
                <Icon icon="mdi:alert-circle-outline" />
                {formError}
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel} onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.btnSubmit}
              disabled={
                isSubmitting ||
                !canSubmitCredentialFormWithDoeyPayout(
                  isEdit,
                  payoutMethod,
                  selectedUser?.doey_user_id ?? doeyUserId,
                  affiliate?.payout_method,
                  affiliate?.doey_user_id
                )
              }
            >
              {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar Afiliado'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function AfiliadosListaPage() {
  const { campaigns } = useInstitutionCampaignSelect()
  const activeCampaigns = useMemo(
    () => filterActiveCampaignsWithSlug(campaigns),
    [campaigns]
  )

  const [affiliates, setAffiliates] = useState<Affiliate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAffiliate, setEditingAffiliate] = useState<Affiliate | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [linksModalAffiliate, setLinksModalAffiliate] = useState<Affiliate | null>(null)
  const [linkDistributorAffiliate, setLinkDistributorAffiliate] = useState<Affiliate | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const affiliatesRes = await fetchAffiliates()
      setAffiliates(affiliatesRes.affiliates)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar afiliados.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = affiliates.filter((a) =>
    a.affiliate_name.toLowerCase().includes(search.toLowerCase()) ||
    a.tracking_code.toLowerCase().includes(search.toLowerCase())
  )

  const copyLink = (affiliate: Affiliate, campaign: AffiliateAllowedCampaign) => {
    const url = buildMyAffiliateLink(campaign, affiliate.tracking_code)
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(affiliate.id)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  const handleModalSubmit = async (
    data: CreateAffiliateInput | UpdateAffiliateInput,
    id?: number
  ) => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      if (id != null) {
        await updateAffiliate(id, data as UpdateAffiliateInput)
      } else {
        await createAffiliate(data as CreateAffiliateInput)
      }
      setIsModalOpen(false)
      setEditingAffiliate(null)
      await load()
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Erro ao salvar afiliado.'
      setSubmitError(msg)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  const openCreate = () => {
    setEditingAffiliate(null)
    setSubmitError(null)
    setIsModalOpen(true)
  }

  const openEdit = (affiliate: Affiliate) => {
    setEditingAffiliate(affiliate)
    setSubmitError(null)
    setIsModalOpen(true)
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <Icon icon="mdi:magnify" className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Buscar afiliado..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className={styles.btnNew} onClick={openCreate}>
          <Icon icon="mdi:plus" />
          Novo Afiliado
        </button>
      </div>

      {submitError && (
        <div className={styles.globalError}>
          <Icon icon="mdi:alert-circle-outline" />
          {submitError}
        </div>
      )}

      {isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando...
        </div>
      ) : error ? (
        <div className={styles.centered}>
          <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
          {error}
          <button className={styles.retryBtn} onClick={() => void load()}>Tentar novamente</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.centered}>
          <Icon icon="mdi:account-group-outline" className={styles.emptyIcon} />
          {search ? 'Nenhum afiliado encontrado para a busca.' : 'Nenhum afiliado ativo. Crie o primeiro!'}
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <HorizontalScrollWithArrows scrollClassName={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th># ID</th>
                <th>Nome</th>
                <th>Link de Indicação</th>
                <th>Indicações</th>
                <th>Pagamento</th>
                <th>Saldo interno</th>
                <th>Comissão total</th>
                <th>%</th>
                <th>Distribuidores</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((affiliate) => {
                const linkCampaigns = affiliate.allowed_campaigns ?? []
                const singleCampaign =
                  linkCampaigns.length === 1 ? linkCampaigns[0] : null
                const singleLink = singleCampaign
                  ? buildMyAffiliateLink(singleCampaign, affiliate.tracking_code)
                  : null
                return (
                  <tr key={affiliate.id}>
                    <td className={styles.idCell}>{affiliate.id}</td>
                    <td>
                      <div className={styles.nameCell}>
                        <span className={styles.affiliateName}>{affiliate.affiliate_name}</span>
                        <span className={styles.trackingCode}>{affiliate.tracking_code}</span>
                      </div>
                    </td>
                    <td>
                      {linkCampaigns.length === 0 ? (
                        <span className={styles.linkPlaceholder}>
                          <Icon icon="mdi:information-outline" />
                          Nenhuma campanha ativa disponível
                        </span>
                      ) : singleLink && singleCampaign ? (
                        <div className={styles.linkCell}>
                          <a
                            href={singleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.linkText}
                            title={singleLink}
                          >
                            {singleLink}
                          </a>
                          <button
                            className={`${styles.copyBtn} ${copiedId === affiliate.id ? styles.copied : ''}`}
                            onClick={() => copyLink(affiliate, singleCampaign)}
                            title="Copiar link"
                          >
                            <Icon icon={copiedId === affiliate.id ? 'mdi:check' : 'mdi:content-copy'} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.viewLinksBtn}
                          onClick={() => setLinksModalAffiliate(affiliate)}
                        >
                          <Icon icon="mdi:link-variant" />
                          Ver links ({linkCampaigns.length})
                        </button>
                      )}
                    </td>
                    <td>
                      <span className={styles.badge}>
                        {affiliate.metrics.payments_count} indicações
                      </span>
                    </td>
                    <td>
                      <span className={styles.payoutBadge}>
                        {affiliate.payout_method === 'doey' ? 'Doey (auto)' : 'Manual'}
                      </span>
                    </td>
                    <td className={styles.amountCell}>
                      {affiliate.payout_method === 'manual'
                        ? formatCurrency(affiliate.balance)
                        : '—'}
                    </td>
                    <td className={styles.amountCell}>
                      {formatCurrency(affiliate.metrics.total_commission)}
                    </td>
                    <td className={styles.commissionCell}>
                      {affiliate.all_campaigns
                        ? `${affiliate.commission_percent}%`
                        : 'Por campanha'}
                    </td>
                    <td>
                      {(affiliate.distributor_registrations ?? []).length === 0
                        ? '—'
                        : (affiliate.distributor_registrations ?? [])
                            .map((r) => r.distributor_name)
                            .filter(Boolean)
                            .join(', ')}
                    </td>
                    <td>
                      <div className={styles.actionCell}>
                        {affiliate.payout_method === 'manual' && (
                          <Link
                            className={styles.saldoLink}
                            to={`/afiliados/saldo?affiliate_id=${affiliate.id}`}
                          >
                            <Icon icon="mdi:wallet-outline" />
                            Saldo
                          </Link>
                        )}
                        <button
                          type="button"
                          className={styles.configBtn}
                          onClick={() => setLinkDistributorAffiliate(affiliate)}
                          title="Vincular a um distribuidor"
                        >
                          <Icon icon="mdi:account-supervisor-outline" />
                          Distribuidor
                        </button>
                        <button
                          className={styles.configBtn}
                          onClick={() => openEdit(affiliate)}
                        >
                          <Icon icon="mdi:cog-outline" />
                          Config
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </HorizontalScrollWithArrows>
          <div className={styles.tableFooter}>
            Mostrando {filtered.length} de {affiliates.length} afiliados
          </div>
        </div>
      )}

      {isModalOpen &&
        createPortal(
          <AffiliateModal
            affiliate={editingAffiliate}
            activeCampaigns={activeCampaigns}
            isSubmitting={isSubmitting}
            onClose={() => { setIsModalOpen(false); setEditingAffiliate(null) }}
            onSubmit={handleModalSubmit}
          />,
          document.body
        )}

      {linksModalAffiliate &&
        createPortal(
          <AffiliateLinksModal
            affiliate={linksModalAffiliate}
            onClose={() => setLinksModalAffiliate(null)}
          />,
          document.body
        )}

      {linkDistributorAffiliate &&
        createPortal(
          <AffiliateLinkDistributorModal
            affiliate={linkDistributorAffiliate}
            onClose={() => setLinkDistributorAffiliate(null)}
            onLinked={() => void load()}
          />,
          document.body
        )}
    </div>
  )
}
